// Radio Clock Builder — default categories/speech types + helpers.
// Colors and durations from the operator's MASTER PROMPT.

export const HOUR_SECONDS = 3600;
export const GAP_COLOR = '#D9D9D9';
// Structure: 7 days × 24 hours = 168 slots. Tab bar shows one day at a time.
// User can leave hours empty — up to 24 per day is the maximum.
export const HOURS_PER_TAB = 24;
export const DAY_LABELS = ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'];
export const DAYS_TOTAL = DAY_LABELS.length; // 7
export const HOURS_TOTAL = DAYS_TOTAL * HOURS_PER_TAB; // 168
export const MAX_ROWS_PER_HOUR = 40;

export const DEFAULT_CATEGORIES = [
  { name: 'Τραγούδι',   sec: 200, color: '#5B9BD5' },
  { name: 'Station ID', sec: 15,  color: '#17365D' },
  { name: 'Jingle',     sec: 15,  color: '#4F81BD' },
  { name: 'Sweeper',    sec: 15,  color: '#4BACC6' },
  { name: 'Promo',      sec: 30,  color: '#C050A0' },
  { name: 'Ομιλία',     sec: 120, color: '#70AD47' },
  { name: 'Break 1',    sec: 300, color: '#C00000' },
  { name: 'Break 2',    sec: 240, color: '#C0392B' },
  { name: 'Break 3',    sec: 180, color: '#E05A47' },
  { name: 'Break 4',    sec: 120, color: '#F07160' },
  { name: 'Weather',    sec: 60,  color: '#FFC000' },
  { name: 'Traffic',    sec: 60,  color: '#F4B183' },
  { name: 'Αθλητικά',   sec: 60,  color: '#ED7D31' },
  { name: 'News',       sec: 90,  color: '#8064A2' },
  { name: 'Άλλο',       sec: 0,   color: '#A5A5A5' },
];

export const DEFAULT_SPEECH_TYPES = [
  { name: 'Start Hour Opening', sec: 30,  color: '#70AD47' },
  { name: 'Main Talk',          sec: 120, color: '#70AD47' },
  { name: 'Talk',               sec: 300, color: '#70AD47' },
  { name: 'Contact Details',    sec: 20,  color: '#70AD47' },
  { name: 'Closing Hour',       sec: 10,  color: '#70AD47' },
  { name: 'News',               sec: 90,  color: '#70AD47' },
];

export const SPEECH_CATEGORY_NAME = 'Ομιλία';

// ---- helpers ----

export function fmt(sec) {
  sec = Math.round(sec || 0);
  const neg = sec < 0; sec = Math.abs(sec);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return (neg ? '-' : '') + (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
}

export function parseDur(str) {
  if (typeof str !== 'string') return 0;
  str = str.trim();
  const p = str.split(':').map((x) => parseInt(x, 10) || 0);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return parseInt(str, 10) || 0;
}

export function catByName(cats, name) {
  return (cats || []).find((c) => c.name === name) || null;
}

export function rowDur(row, cats, speech) {
  if (!row || !row.cat) return 0;
  if (row.cat === SPEECH_CATEGORY_NAME && row.type) {
    const sp = (speech || []).find((s) => s.name === row.type);
    if (sp) return sp.sec;
  }
  const c = catByName(cats, row.cat);
  return c ? c.sec : 0;
}

export function computeHour(rows, cats, speech) {
  // Returns { total, remaining, over, segments: [{cat, type, dur, from, to, color}] }
  let run = 0;
  const segments = [];
  (rows || []).forEach((r) => {
    const dur = rowDur(r, cats, speech);
    if (!r.cat || dur <= 0) return;
    const c = catByName(cats, r.cat);
    segments.push({
      cat: r.cat,
      type: r.type || '',
      dur,
      from: run,
      to: run + dur,
      color: c ? c.color : '#888',
    });
    run += dur;
  });
  const total = run;
  const remaining = HOUR_SECONDS - total;
  return {
    total,
    remaining,
    over: total > HOUR_SECONDS,
    segments,
  };
}

export function emptyHour() {
  return { rows: [] };
}

// True if the hour has at least one row with a chosen category.
export function hasHourContent(hour, cats, speech) {
  if (!hour || !Array.isArray(hour.rows)) return false;
  return hour.rows.some((r) => rowDur(r, cats, speech) > 0);
}

// Return the ordered list of {index, hour, day, hourInDay} for hours
// that actually contain something. Used by the PDF export so empty
// clocks never make it into the file.
export function filledClocks(state) {
  const cats = state.categories;
  const speech = state.speech_types;
  const out = [];
  (state.hours || []).forEach((h, i) => {
    if (hasHourContent(h, cats, speech)) {
      const day = Math.floor(i / HOURS_PER_TAB);
      const hourInDay = (i % HOURS_PER_TAB) + 1;
      out.push({ index: i, hour: h, day, hourInDay });
    }
  });
  return out;
}

export function makeDefaultState() {
  return {
    categories: DEFAULT_CATEGORIES.map((c) => ({ ...c })),
    speech_types: DEFAULT_SPEECH_TYPES.map((c) => ({ ...c })),
    hours: Array.from({ length: HOURS_TOTAL }, () => emptyHour()),
  };
}

// ---- aggregate stats across all 30 hours ----
export function aggregateStats(state) {
  const map = new Map(); // catName -> {sec, color}
  let totalUsed = 0;
  (state.hours || []).forEach((h) => {
    const { segments } = computeHour(h.rows || [], state.categories, state.speech_types);
    segments.forEach((s) => {
      const prev = map.get(s.cat) || { sec: 0, color: s.color };
      prev.sec += s.dur;
      prev.color = s.color;
      map.set(s.cat, prev);
      totalUsed += s.dur;
    });
  });
  const items = Array.from(map.entries())
    .map(([name, v]) => ({ name, sec: v.sec, color: v.color }))
    .sort((a, b) => b.sec - a.sec);
  return { items, totalUsed };
}
